import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TrackerMilestoneBreadcrumbs,
    TrackerMilestoneCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrackerMilestoneActions from "../../../actions/trackerMilestones";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./TrackerMilestonePage.scss";

interface TrackerMilestonePageProps {
    form: Dtos.TrackerMilestone & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trackerMilestone: Dtos.TrackerMilestone;
    lookups: Dtos.Lookup[];
    loadingTrackerMilestone: boolean;
    loadTrackerMilestoneFailure: boolean;
    loadTrackerMilestoneErrorCode: string;
    savingTrackerMilestone: boolean;
    saveTrackerMilestoneSuccess: boolean;
    saveTrackerMilestoneFailure: boolean;
    creatingTrackerMilestone: boolean;
    createTrackerMilestoneSuccess: boolean;
    validatingTrackerMilestone: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrackerMilestone: TrackerMilestoneActions.ITrackerMilestoneClearActionCreator;
    loadTrackerMilestone: TrackerMilestoneActions.ITrackerMilestoneLoadByIdActionCreator;
    saveTrackerMilestone: TrackerMilestoneActions.ITrackerMilestoneSaveActionCreator;
    createTrackerMilestone: TrackerMilestoneActions.ITrackerMilestoneCreateActionCreator;
    validateTrackerMilestone: TrackerMilestoneActions.ITrackerMilestoneValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trackerMilestone";

const TrackerMilestonePage: FC<TrackerMilestonePageProps> = ({
    form,
    formState,
    formProperties,
    trackerMilestone,
    lookups,
    loadingTrackerMilestone,
    loadTrackerMilestoneFailure,
    loadTrackerMilestoneErrorCode,
    savingTrackerMilestone,
    saveTrackerMilestoneSuccess,
    saveTrackerMilestoneFailure,
    creatingTrackerMilestone,
    createTrackerMilestoneSuccess,
    validatingTrackerMilestone,
    validationFailures,
    permissions,
    clearTrackerMilestone,
    loadTrackerMilestone,
    saveTrackerMilestone,
    createTrackerMilestone,
    validateTrackerMilestone,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trackerMilestoneId } = useParams<{ trackerMilestoneId: string }>();
    const trackerMilestoneIdNumber = parseInt(trackerMilestoneId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrackerMilestone = {} as Dtos.TrackerMilestone;

        loadForm(
            reduxFormName,
            Object.assign({ ...trackerMilestone }, extraData)
        );
    };


    // Load tracker milestone
    useEffect(() => {
        if (
            trackerMilestoneIdNumber &&
            !trackerMilestone &&
            !loadingTrackerMilestone
        ) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(
                TrackerMilestoneBreadcrumbs(trackerMilestoneIdNumber)
            );
            loadTrackerMilestone(trackerMilestoneIdNumber);
        } else if (
            !trackerMilestoneIdNumber &&
            !creatingTrackerMilestone &&
            !createTrackerMilestoneSuccess
        ) {
            createTrackerMilestone();
            setBreadcrumbs(TrackerMilestoneCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trackerMilestone && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trackerMilestoneIdNumber,
        trackerMilestone,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TrackerMilestoneBreadcrumbs,
        loadTrackerMilestone,
        createTrackerMilestone,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrackerMilestone,
        creatingTrackerMilestone,
        createTrackerMilestoneSuccess,
        TrackerMilestoneCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrackerMilestoneFailure) {
            navigate(`/error/${loadTrackerMilestoneErrorCode}`);
        }
    }, [loadTrackerMilestoneFailure, loadTrackerMilestoneErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trackerMilestoneIdNumber && trackerMilestone?.value) {
            setBreadcrumbs(
                TrackerMilestoneBreadcrumbs(
                    trackerMilestoneIdNumber,
                    trackerMilestone?.value
                )
            );
        }
    }, [
        trackerMilestoneIdNumber,
        trackerMilestone,
        setBreadcrumbs,
        TrackerMilestoneBreadcrumbs,
    ]);

    // Reload tracker milestone after saving
    useEffect(() => {
        if (!savingTrackerMilestone && saveTrackerMilestoneSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Tracker milestone saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/trackermilestones";
                navigate(url);
            } else if (!trackerMilestoneIdNumber) {
                let url: string = `/settings/qa/trackermilestones/${trackerMilestone?.id}`;
                navigate(url);
                clearTrackerMilestone();
            }
        }
    }, [
        savingTrackerMilestone,
        saveTrackerMilestoneSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrackerMilestone && saveTrackerMilestoneFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrackerMilestone, saveTrackerMilestoneFailure]);

    // Cleanup effect to clear the tracker milestone on component unmount
    useEffect(() => {
        return () => {
            clearTrackerMilestone();
            resetForm(reduxFormName);
        };
    }, [clearTrackerMilestone, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/trackermilestones";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrackerMilestone) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrackerMilestone = {} as Dtos.TrackerMilestone;

            await saveTrackerMilestone(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrackerMilestone = {} as Dtos.TrackerMilestone;

            if (form && hasBeenSubmitted && !validatingTrackerMilestone) {
                await validateTrackerMilestone(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TaskTypesUpdate,
                Dtos.Permission.TaskTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrackerMilestone ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trackerMilestone",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trackerMilestone",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "trackerMilestone",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trackerMilestone",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrackerMilestone,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingTrackerMilestone}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrackerMilestonePageProps> => {
    let trackerMilestone: Dtos.TrackerMilestone | undefined = !(
        state.trackerMilestones.formData instanceof Array
    )
        ? state.trackerMilestones.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trackerMilestone: trackerMilestone!,
        loadingTrackerMilestone:
            state.trackerMilestones.loadState &&
            state.trackerMilestones.loadState.status == RequestState.Pending,
        loadTrackerMilestoneFailure:
            state.trackerMilestones.loadState &&
            state.trackerMilestones.loadState.status == RequestState.Failure,
        loadTrackerMilestoneErrorCode:
            state.trackerMilestones.loadState.errorCode,
        savingTrackerMilestone:
            state.trackerMilestones.saveState &&
            state.trackerMilestones.saveState.status == RequestState.Pending,
        saveTrackerMilestoneSuccess:
            state.trackerMilestones.saveState &&
            state.trackerMilestones.saveState.status == RequestState.Success,
        saveTrackerMilestoneFailure:
            state.trackerMilestones.saveState &&
            state.trackerMilestones.saveState.status == RequestState.Failure,
        creatingTrackerMilestone:
            state.trackerMilestones.createState &&
            state.trackerMilestones.createState.status == RequestState.Pending,
        createTrackerMilestoneSuccess:
            state.trackerMilestones.createState &&
            state.trackerMilestones.createState.status == RequestState.Success,
        validatingTrackerMilestone:
            state.trackerMilestones.validateState &&
            state.trackerMilestones.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.trackerMilestone,
        formState: state.trackerMilestones.formState,
        formProperties: state.trackerMilestones.formProperties,
        validationFailures: trackerMilestone
            ? state.trackerMilestones.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrackerMilestonePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrackerMilestone: bindActionCreators(
            TrackerMilestoneActions.LoadTrackerMilestoneById,
            dispatch
        ),
        clearTrackerMilestone: bindActionCreators(
            TrackerMilestoneActions.Clear,
            dispatch
        ),
        saveTrackerMilestone: bindActionCreators(
            TrackerMilestoneActions.SaveTrackerMilestone,
            dispatch
        ),
        createTrackerMilestone: bindActionCreators(
            TrackerMilestoneActions.CreateTrackerMilestone,
            dispatch
        ),
        validateTrackerMilestone: bindActionCreators(
            TrackerMilestoneActions.ValidateTrackerMilestone,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrackerMilestonePage);
